import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getCurrentReportPrice, getPlatformSettings } from '../utils/platformSettingsService';
import { sanitizeError } from '../utils/errorSanitizer';

// Stripe publishable key - use environment variable or fallback to hardcoded key
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51SP75vDmL008p75tjTX1FnBAk6YZEKWZ7vmGIhfRZ7ZPr6c8EjUAg3KI3N5OKstnkPgA6o5S4zFRztWceLwwgrSk00SGhWSiwk';

// Initialize Stripe with error handling
let stripePromise = null;
if (STRIPE_KEY && STRIPE_KEY.startsWith('pk_')) {
  try {
    stripePromise = loadStripe(STRIPE_KEY);
  } catch (error) {
    console.error('❌ Failed to initialize Stripe:', error);
    stripePromise = null;
  }
} else {
  console.error('❌ Invalid or missing Stripe publishable key');
  stripePromise = null;
}

// Card Element styling
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#fafafa',
      fontFamily: 'monospace',
      fontSize: '16px',
      '::placeholder': {
        color: '#737373',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

// Payment Form Component (uses Stripe Elements)
const PaymentForm = ({ forecastData, careerReportData, reportType, onPaymentSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [cognitivePrice, setCognitivePrice] = useState(79);
  const [careerPrice, setCareerPrice] = useState(79);
  const [bundlePrice, setBundlePrice] = useState(149);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [addBundle, setAddBundle] = useState(false);

  // Determine the initial report type
  const initialReportType = reportType || (forecastData ? 'cognitive' : 'career');
  const [selectedReportType, setSelectedReportType] = useState(initialReportType);

  // Load platform settings and pricing
  useEffect(() => {
    async function loadPricing() {
      const settings = await getPlatformSettings();
      setPlatformSettings(settings);
      const cogPrice = await getCurrentReportPrice(settings);
      setCognitivePrice(cogPrice);
      // Use ?? instead of || to properly handle $0 prices
      setCareerPrice(settings?.careerReportPrice ?? 79);
      setBundlePrice(settings?.bundlePrice ?? 149);
    }
    loadPricing();
  }, []);

  // Calculate current price
  const calculatePrice = () => {
    if (addBundle || selectedReportType === 'bundle') {
      return bundlePrice;
    }
    if (selectedReportType === 'career' || careerReportData) {
      return careerPrice;
    }
    return cognitivePrice;
  };

  const currentPrice = calculatePrice();
  const canAddBundle = !addBundle && (forecastData || careerReportData) && selectedReportType !== 'bundle';

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      console.log('💳 Processing payment...');
      
      // Determine final report type
      const finalReportType = addBundle ? 'bundle' : selectedReportType;
      
      // Validate we have the required IDs
      if (!forecastData?.forecastId && !careerReportData?.careerReportId) {
        throw new Error('No report ID found. Please complete the wizard first.');
      }

      console.log('🆔 Report Type:', finalReportType);
      console.log('🆔 Forecast ID:', forecastData?.forecastId);
      console.log('🆔 Career Report ID:', careerReportData?.careerReportId);

      // Step 1: Create PaymentIntent via Cloud Function
      const functions = getFunctions();
      const createPaymentIntent = httpsCallable(functions, 'createPaymentIntent');
      
      const paymentData = {
        amount: currentPrice * 100, // Convert dollars to cents
        reportType: finalReportType,
      };

      // Add IDs based on report type
      console.log('📊 Payment data preparation:', {
        finalReportType,
        forecastData,
        careerReportData
      });

      if (finalReportType === 'bundle') {
        if (forecastData?.forecastId) paymentData.forecastId = forecastData.forecastId;
        if (careerReportData?.careerReportId) {
          paymentData.careerReportId = careerReportData.careerReportId;
        } else if (careerReportData?.reportId) {
          paymentData.careerReportId = careerReportData.reportId;
        }
      } else if (finalReportType === 'career') {
        // Try different possible field names
        paymentData.careerReportId = careerReportData?.careerReportId 
                                    || careerReportData?.reportId 
                                    || careerReportData?.id;
        
        if (!paymentData.careerReportId) {
          console.error('❌ No career report ID found in data:', careerReportData);
          throw new Error('Career report ID not found. Please complete the wizard first.');
        }
      } else {
        paymentData.forecastId = forecastData.forecastId;
      }
      
      console.log('💳 Sending payment data:', paymentData);

      const result = await createPaymentIntent(paymentData);

      console.log('✅ PaymentIntent created:', result.data.clientSecret);

      if (!result.data.clientSecret) {
        throw new Error('Failed to create payment intent');
      }

      // Step 2: Confirm payment with card details
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        result.data.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      console.log('✅ Payment successful!', paymentIntent);

      // Call success handler
      if (onPaymentSuccess) {
        onPaymentSuccess(paymentIntent, finalReportType);
      }

    } catch (err) {
      console.error('Payment error:', err);
      setError(sanitizeError(err) || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <Paper
            sx={{
              p: 4,
              backgroundColor: '#171717',
              border: '1px solid #262626',
              borderRadius: '12px'
            }}
          >
            {/* Header */}
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'monospace',
                color: '#fafafa',
                mb: 1
              }}
            >
              Complete Purchase
            </Typography>

            {/* Price */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h2"
                sx={{
                  fontFamily: 'monospace',
                  color: '#fafafa',
                  fontWeight: 300
                }}
              >
                ${currentPrice}
              </Typography>
              <Typography variant="body2" sx={{ color: '#737373' }}>
                {addBundle || selectedReportType === 'bundle' 
                  ? 'Both Reports Bundle • Save $' + ((cognitivePrice + careerPrice) - bundlePrice)
                  : 'One-time payment'} • Secure processing by Stripe
              </Typography>
            </Box>

            {/* Bundle Option */}
            {canAddBundle && (
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  backgroundColor: '#0a0a0a',
                  border: addBundle ? '2px solid #9D00FF' : '1px solid #262626',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#9D00FF'
                  }
                }}
                onClick={() => setAddBundle(!addBundle)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addBundle}
                      onChange={(e) => setAddBundle(e.target.checked)}
                      sx={{
                        color: '#525252',
                        '&.Mui-checked': {
                          color: '#9D00FF'
                        }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ color: '#fafafa', fontWeight: 600, fontFamily: 'monospace' }}>
                        Add {selectedReportType === 'cognitive' ? 'Career Path Report' : 'Cognitive Profile'} - Save ${(cognitivePrice + careerPrice) - bundlePrice}!
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#737373' }}>
                        Get both reports for ${bundlePrice} (normally ${cognitivePrice + careerPrice})
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Paper>
            )}

            <Divider sx={{ my: 3, borderColor: '#262626' }} />

            {/* Card Input */}
            <Typography
              variant="body1"
              sx={{
                color: '#fafafa',
                mb: 2,
                fontFamily: 'monospace',
                fontWeight: 600
              }}
            >
              Payment Information
            </Typography>

            <Box
              sx={{
                p: 2,
                backgroundColor: '#0a0a0a',
                border: '1px solid #262626',
                borderRadius: '8px',
                mb: 3
              }}
            >
              <CardElement
                options={CARD_ELEMENT_OPTIONS}
                onChange={(e) => setCardComplete(e.complete)}
              />
            </Box>

            {/* Error Display */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #ef4444',
                  color: '#fafafa'
                }}
              >
                {error}
              </Alert>
            )}

            {/* Payment Button */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={!stripe || processing || !cardComplete}
              sx={{
                py: 1.5,
                fontFamily: 'monospace',
                fontSize: '1.1rem',
                backgroundColor: '#fafafa',
                color: '#0a0a0a',
                mb: 2,
                '&:hover': {
                  backgroundColor: '#e5e5e5'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#262626',
                  color: '#525252'
                }
              }}
            >
              {processing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} sx={{ color: '#525252' }} />
                  <span>Processing...</span>
                </Box>
              ) : (
                `Pay $${currentPrice}`
              )}
            </Button>

            {/* Cancel Button */}
            <Button
              fullWidth
              variant="outlined"
              onClick={onCancel}
              disabled={processing}
              sx={{
                color: '#737373',
                borderColor: '#262626',
                fontFamily: 'monospace',
                '&:hover': {
                  borderColor: '#525252',
                  backgroundColor: '#0a0a0a'
                }
              }}
            >
              Go Back
            </Button>

            {/* Security Notice */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: '#525252', fontFamily: 'monospace' }}>
                🔒 Secure • PCI-DSS Compliant • Powered by Stripe
              </Typography>
            </Box>

            {/* Disclaimer - different for each report type */}
            <Alert
              severity="info"
              sx={{
                mt: 3,
                backgroundColor: '#0a0a0a',
                border: '1px solid #262626',
                color: '#737373'
              }}
            >
              <Typography variant="caption">
                {(selectedReportType === 'career' || careerReportData) && !forecastData ? (
                  // Career Report disclaimer
                  <>
                    Career guidance based on current job market data. Not a guarantee of employment.
                    Your resume data is used only to generate your report.
                  </>
                ) : selectedReportType === 'bundle' || addBundle ? (
                  // Bundle disclaimer
                  <>
                    For career planning and self-reflection. Not professional career counseling or medical advice.
                    Your data is processed securely and used only to generate your reports.
                  </>
                ) : (
                  // Cognitive Profile disclaimer (original)
                  <>
                For reflection and entertainment. Not medical, financial, or professional advice.
                    Your chat log is processed and then permanently deleted.
                  </>
                )}
              </Typography>
            </Alert>
          </Paper>
        </form>
      </Container>
    </Box>
  );
};

// Wrapper component that provides Stripe Elements context
const PaymentScreen = ({ forecastData, careerReportData, reportType, onPaymentSuccess, onCancel }) => {
  const [stripeLoadError, setStripeLoadError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [localStripePromise, setLocalStripePromise] = useState(stripePromise);

  useEffect(() => {
    // Check if Stripe loaded successfully
    if (localStripePromise) {
      localStripePromise
        .then((stripe) => {
          if (!stripe) {
            console.error('❌ Stripe failed to load');
            setStripeLoadError(true);
          }
        })
        .catch((error) => {
          console.error('❌ Stripe loading error:', error);
          setStripeLoadError(true);
        });
    } else {
      setStripeLoadError(true);
    }
  }, [localStripePromise]);

  const handleRetry = () => {
    console.log('🔄 Retrying Stripe initialization...');
    setIsRetrying(true);
    setStripeLoadError(false);
    
    if (!STRIPE_KEY || !STRIPE_KEY.startsWith('pk_')) {
      console.error('❌ Cannot retry: Invalid or missing Stripe key');
      setStripeLoadError(true);
      setIsRetrying(false);
      return;
    }
    
    try {
      const newStripePromise = loadStripe(STRIPE_KEY);
      setLocalStripePromise(newStripePromise);
      
      newStripePromise
        .then((stripe) => {
          if (stripe) {
            console.log('✅ Stripe loaded successfully on retry');
            setStripeLoadError(false);
          } else {
            console.error('❌ Stripe still failed to load');
            setStripeLoadError(true);
          }
          setIsRetrying(false);
        })
        .catch((error) => {
          console.error('❌ Retry failed:', error);
          setStripeLoadError(true);
          setIsRetrying(false);
        });
    } catch (error) {
      console.error('❌ Retry error:', error);
      setStripeLoadError(true);
      setIsRetrying(false);
    }
  };

  // Show error state if Stripe failed to load
  if (stripeLoadError) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
        <Container maxWidth="sm">
          <Paper
            sx={{
              p: 4,
              backgroundColor: '#171717',
              border: '1px solid #262626',
              borderRadius: '12px'
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'monospace',
                color: '#fafafa',
                mb: 3
              }}
            >
              Payment System Unavailable
            </Typography>

            <Alert
              severity="error"
              sx={{
                mb: 3,
                backgroundColor: '#0a0a0a',
                border: '1px solid #ef4444',
                color: '#fafafa'
              }}
            >
              <Typography variant="body1" sx={{ mb: 2 }}>
                We're having trouble connecting to our payment processor (Stripe). This could be due to:
              </Typography>
              <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                • Network connectivity issues<br/>
                • Browser extensions blocking payment scripts<br/>
                • Ad blockers or privacy tools<br/>
                • Firewall or security settings
              </Typography>
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 2 }}>
                <strong>Troubleshooting steps:</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: '#737373', pl: 2 }}>
                1. Disable ad blockers or browser extensions temporarily<br/>
                2. Check your internet connection<br/>
                3. Try a different browser<br/>
                4. Ensure cookies and JavaScript are enabled
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleRetry}
                disabled={isRetrying}
                sx={{
                  py: 1.5,
                  fontFamily: 'monospace',
                  fontSize: '1rem',
                  backgroundColor: '#00FFF5',
                  color: '#0a0a0a',
                  '&:hover': {
                    backgroundColor: '#00CCC4'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#262626',
                    color: '#525252'
                  }
                }}
              >
                {isRetrying ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} sx={{ color: '#525252' }} />
                    <span>Retrying...</span>
                  </Box>
                ) : (
                  '🔄 Retry Connection'
                )}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={onCancel}
                sx={{
                  color: '#737373',
                  borderColor: '#262626',
                  fontFamily: 'monospace',
                  '&:hover': {
                    borderColor: '#525252',
                    backgroundColor: '#0a0a0a'
                  }
                }}
              >
                Go Back
              </Button>
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: '#525252', fontFamily: 'monospace' }}>
                Your progress has been saved. You can complete payment later from your dashboard.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Elements stripe={localStripePromise}>
      <PaymentForm
        forecastData={forecastData}
        careerReportData={careerReportData}
        reportType={reportType}
        onPaymentSuccess={onPaymentSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
};

export default PaymentScreen;

